<template>
  <div class="special-case-step col-lg-10">
    <section class="step-group">
      <h3>{{ $t("tools.special_cases.work_timing") }}</h3>
      <div class="step-fields">
        <div class="editor-field">
          <label>{{ $t("tools.import_validator.start_date") }}</label>
          <DxDateBox
            :value="localData.work_started_at"
            type="datetime"
            display-format="yyyy-MM-dd HH:mm"
            date-serialization-format="yyyy-MM-ddTHH:mm:ss"
            :disabled="saving"
            @value-changed="setValue('work_started_at', $event.value)"
          />
        </div>
        <div class="editor-field">
          <label>{{ $t("tools.import_validator.end_date") }}</label>
          <DxDateBox
            :value="localData.work_ended_at"
            type="datetime"
            display-format="yyyy-MM-dd HH:mm"
            date-serialization-format="yyyy-MM-ddTHH:mm:ss"
            :disabled="saving"
            @value-changed="setValue('work_ended_at', $event.value)"
          />
        </div>
        <div v-for="field in durationFields" :key="field[0]" class="editor-field">
          <label>{{ $t(field[1]) }} ({{ $t("tools.special_cases.minutes") }})</label>
          <DxNumberBox
            :value="localData[field[0]]"
            :min="0"
            :max="50339"
            :step="1"
            :show-spin-buttons="true"
            :disabled="saving"
            @value-changed="setValue(field[0], $event.value)"
          />
        </div>
      </div>
    </section>
    <section class="step-group">
      <h3>{{ $t("tools.special_cases.work_values") }}</h3>
      <div class="step-fields">
        <div v-for="field in numericFields" :key="field[0]" class="editor-field">
          <DxCheckBox
            v-if="field[0] === 'net_surface_worked'"
            class="target-surface-checkbox"
            :value="!!localData.use_target_net_surface"
            :text="$t(mode === 'serve' ? 'tools.special_cases.use_work_net_surface' : 'tools.special_cases.use_parcel_surface')"
            :disabled="saving"
            @value-changed="setValue('use_target_net_surface', $event.value)"
          />
          <label>{{ $t(field[1]) }}</label>
          <DxNumberBox
            :value="localData[field[0]]"
            :min="0"
            :show-spin-buttons="true"
            :disabled="saving || (field[0] === 'net_surface_worked' && !!localData.use_target_net_surface)"
            @value-changed="setValue(field[0], $event.value)"
          />
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import moment from "moment";
import DxDateBox from "devextreme-vue/date-box";
import DxNumberBox from "devextreme-vue/number-box";
import DxCheckBox from "devextreme-vue/check-box";
import { numericFields, durationFields } from "../fields";

export default {
  name: "SpecialCaseWorkMeasurementsStep",
  components: { DxDateBox, DxNumberBox, DxCheckBox },
  props: {
    formData: { type: Object, required: true },
    mode: { type: String, default: "assign" },
    saving: { type: Boolean, default: false },
  },
  data() {
    return { localData: { ...this.formData }, numericFields, durationFields };
  },
  watch: {
    formData: {
      deep: true,
      handler(value) {
        if (JSON.stringify(this.localData) !== JSON.stringify(value)) this.localData = { ...value };
      },
    },
  },
  methods: {
    setValue(field, value) {
      this.$set(this.localData, field, value);
      this.$emit("input", { [field]: value });
    },
    validate() {
      const errors = [];
      const startedAt = this.localData.work_started_at;
      const endedAt = this.localData.work_ended_at;
      if (!startedAt || !endedAt || !moment(startedAt).isValid() || !moment(endedAt).isValid() || moment(endedAt).isBefore(startedAt)) {
        errors.push(this.$t("tools.special_cases.invalid_dates"));
      }
      [...numericFields, ...durationFields].forEach(([field, label]) => {
        if (field === "net_surface_worked" && this.localData.use_target_net_surface) return;
        const value = Number(this.localData[field]);
        if (!Number.isFinite(value) || value < 0) {
          errors.push(this.$t("tools.special_cases.required", { field: this.$t(label) }));
        }
      });
      if (durationFields.every(([field]) => !Number(this.localData[field]))) errors.push(this.$t("tools.special_cases.duration_required"));
      return errors;
    },
  },
};
</script>

<style lang="scss" scoped>
.special-case-step {
  display: flex;
  flex-direction: column;
  gap: 20px;
}
.step-group {
  padding-bottom: 18px;
  border-bottom: 1px solid #dedede;
}
.step-group h3 {
  margin: 0 0 12px;
  font-size: 17px;
}
.step-fields {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
}
.editor-field {
  min-width: 0;
}
.target-surface-checkbox {
  margin-bottom: 8px;
}
.editor-field label {
  display: block;
  margin-bottom: 5px;
}
@media (max-width: 680px) {
  .step-fields {
    grid-template-columns: minmax(0, 1fr);
  }
}
</style>